export interface CaseHighlight {
  icon: string;
  title: string;
  desc: string;
}

export interface CaseProcessStep {
  phase: string;
  title: string;
  desc: string;
}

export interface CaseDetail {
  slug: string;
  name: string;
  eyebrow: string;
  tag: string;
  status: string;
  region: string;
  industry: string;
  date: string;
  title: string;
  titleHighlight: string;
  subtitle: string;
  desc: string;
  heroImage: string;
  heroAlt: string;
  overview: string;
  highlights: CaseHighlight[];
  background: string;
  demand: string;
  plan: string;
  tech: string[];
  solutionDiagram?: {
    title: string;
    desc: string;
    images: { src: string; alt: string; caption: string; note: string }[];
  };
  practice?: {
    title: string;
    paragraphs: string[];
    images: { src: string; alt: string; caption: string }[];
  };
  process: CaseProcessStep[];
  results: string[];
  resultsNote: string;
  copyable: string;
  solutions: { label: string; href: string }[];
}

export const caseDetails: CaseDetail[] = [
  {
    slug: 'lieting-smart-parking',
    name: '铼停科技自由流停车',
    eyebrow: '客户案例 · 自由流停车',
    tag: '智慧交通 · 停车运营',
    status: '推进中',
    region: '中国 · 城市停车运营场景',
    industry: '自由流停车 · 交通',
    date: '2026.08',
    title: '铼停科技自由流停车',
    titleHighlight: '车场物联 · 统一运营 · 数据驱动',
    subtitle: '以停车物联、统一运营与数据分析能力，连接车场、车位、车辆和服务。',
    desc: '面向园区、商业综合体与公共停车场，构建覆盖通行、引导、收费、运营的自由流停车体系，让每一次进出更顺畅，让每一个车位更高效。',
    heroImage:
      'https://readdy.ai/api/search-image?query=Stylized%20smart%20parking%20scene%20with%20a%20modern%20parking%20garage%20entrance%20at%20dusk%2C%20connected%20parking%20sensors%20and%20digital%20guidance%20screens%20showing%20available%20spaces%2C%20light%20trails%20of%20vehicles%20flowing%20along%20a%20city%20road%2C%20deep%20graphite%20background%20with%20warm%20amber%20and%20muted%20green%20accents%2C%20clean%20minimal%20editorial%20illustration%2C%20no%20text%2C%20no%20people%2C%20high%20detail%2C%20harmonious%20composition&width=1600&height=900&seq=case-lieting-parking-hero-01&orientation=landscape',
    heroAlt: '铼停科技自由流停车客户案例',
    overview:
      '铼停科技聚焦城市停车运营场景，希望通过数字化手段解决车主找位难、进出场拥堵、收费对账繁琐等实际痛点。华辉数智以停车物联、统一运营平台与数据分析能力，帮助其把车场、车位、车辆和服务连接起来，构建高效、顺畅且可持续优化的停车服务体系。',
    highlights: [
      {
        icon: 'ri-building-2-line',
        title: '覆盖多类车场',
        desc: '适配园区与写字楼、商业综合体、公共停车场等不同运营场景。',
      },
      {
        icon: 'ri-node-tree',
        title: '全链路连接',
        desc: '从车场接入、运营管理到用户服务，打通停车业务完整链路。',
      },
      {
        icon: 'ri-dashboard-3-line',
        title: '统一运营',
        desc: '多场库、多设备集中管理，收费规则与经营数据统一汇总。',
      },
      {
        icon: 'ri-cloud-off-line',
        title: '断网可用',
        desc: '边缘计算保障网络异常时核心通行与计费仍可持续运行。',
      },
    ],
    background:
      '随着城市车辆保有量持续增长，停车资源的调度与运营效率成为影响出行体验的重要环节。铼停科技服务多类城市停车场景，面对多品牌设备接口不统一、多场库分散管理、收费规则复杂等现实问题，希望以平台化方式实现统一运营。',
    demand:
      '在保障车主通行体验的前提下，实现车场设备统一接入、收费与优惠规则灵活配置、多场库集中管理、经营数据可分析，减少人工收费与对账工作量，并让异常情况能够及时被发现与处理。',
    plan:
      '华辉数智以运营平台为核心，向上连接用户服务、向下接入车场设备，构建覆盖通行、引导、收费、运营的停车能力底座。平台统一汇聚车流、车位、订单、收入与设备数据，形成车场经营全景，为精细化管理与持续优化提供依据。',
    tech: [
      '停车物联',
      '车牌识别',
      '车位检测',
      '车位引导',
      '无感支付',
      '统一运营平台',
      '数据分析',
      '边缘计算',
    ],
    solutionDiagram: {
      title: '路内停车整体解决方案',
      desc: '以云服务器为核心，连接手机 APP、管理平台、基站与巡检员等多端角色，向下对接路侧车辆信息采集设备，向上承载停车智能化服务、移动支付与低功耗智能采集能力，形成覆盖路内停车全流程的整体解决方案。',
      images: [
        {
          src: 'https://www.szrepark.com/img/1.1b402165.png',
          alt: '路内停车整体解决方案架构图，云服务器连接手机APP、管理平台、基站与巡检员',
          caption: '路内停车整体解决方案',
          note: '云端协同 · 多端联动 · 路侧采集',
        },
        {
          src: 'https://www.szrepark.com/img/2.ba94a1b0.png',
          alt: '泊车业务架构图，包含停车智能化服务、移动支付与车辆信息低功耗智能采集',
          caption: '泊车业务能力架构',
          note: '智能服务 · 移动支付 · 低功耗采集',
        },
      ],
    },
    practice: {
      title: '广东东莞',
      paragraphs: [
        '2019 年 12 月，当地铺设新型低位云视频设备，为市民提供路边临时停车服务，缓解「停车难」问题。',
        '结合新型智能化停车设备，适当扩大路内、路外停车收费差距，合理调节停车服务资源的利用，通过「差异化收费」引导车主「快停快走」，在一定程度上缓解了道路交通拥堵情况。',
      ],
      images: [
        {
          src: 'https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/0b85aaabd51837b2fb19cacb6fd749ba.png',
          alt: '广东东莞路边停车泊位，车辆规范停放，路侧安装低位云视频识别设备',
          caption: '图1 · 低位云视频设备',
        },
        {
          src: 'https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/6b27f526c9ba1742118f7636373f8e96.png',
          alt: '广东东莞路内停车位，车辆停放于划线泊位，路侧设有低位智能识别设备',
          caption: '图2 · 路内规范泊位',
        },
        {
          src: 'https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/1e1122b97fc962cc331426de4a427679.png',
          alt: '广东东莞路边停车带，多辆汽车依次停放于泊位，路侧低位设备进行车辆识别',
          caption: '图3 · 车辆有序停放',
        },
      ],
    },
    process: [
      { phase: '调研', title: '场库调研', desc: '梳理车位、车道、设备、网络和收费规则，明确运营目标。' },
      { phase: '设计', title: '方案设计', desc: '确定软硬件配置、接口方案、运营流程与验收标准。' },
      { phase: '建设', title: '系统建设', desc: '部署运营平台、边缘接入与出入口通行、支付能力。' },
      { phase: '联调', title: '联调试运行', desc: '覆盖高峰、断网、异常收费与对账等关键场景验证。' },
      { phase: '运营', title: '持续运营', desc: '通过经营看板与设备运维数据持续优化停车服务。' },
    ],
    results: [
      '完成多类停车场景的业务梳理与统一接入方案',
      '车道通行与收费规则实现平台化统一配置',
      '多场库经营数据可汇总查看与分析',
      '异常订单与设备状态可被及时发现处理',
    ],
    resultsNote:
      '以上为项目推进过程中的阶段性工作说明，具体指标以双方确认的正式资料为准，不代表最终验收结果。',
    copyable:
      '以运营平台为核心、以边缘计算保障现场可用、以数据看板支撑精细运营，是停车场景数字化升级可复用的路径，可较快迁移到不同类型的车场。',
    solutions: [{ label: '自由流停车解决方案', href: '/solutions/smart-parking' }],
  },
];